import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#26ab4d]/25 via-black to-[#1e8a3f]/25"></div>
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 20% 30%, rgba(38, 171, 77, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(30, 138, 63, 0.15) 0%, transparent 50%)',
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <div className="container mx-auto px-[1.25rem] sm:px-[2rem] pt-[7rem] sm:pt-[9rem] lg:pt-[10rem] pb-[4rem] sm:pb-[6rem] lg:pb-[8rem]">
          <div className="max-w-4xl mx-auto text-white/85 leading-relaxed [overflow-wrap:anywhere]">
            {/* Header */}
            <div className="mb-10 sm:mb-12 border-b border-white/10 pb-6 sm:pb-8">
              <h1 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.75rem] font-black text-white mb-3 leading-tight">
                Privacy{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">
                  Policy
                </span>
              </h1>
              <div className="space-y-1 text-[0.8125rem] sm:text-[0.9375rem] text-white/70">
                <p>
                  <span className="text-white/90 font-semibold">Last updated:</span> 1 July 2026
                </p>
                <p>
                  <span className="text-white/90 font-semibold">Legal entity:</span> Zarito Data
                  Solutions Private Limited (trading as Bargad.ai)
                </p>
                <p>
                  <span className="text-white/90 font-semibold">Registered &amp; Governing Law:</span>{' '}
                  India
                </p>
              </div>
            </div>

            <div className="space-y-8 sm:space-y-10 text-[0.875rem] sm:text-[0.9375rem]">
              {/* Preface */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  Preface
                </h2>
                <div className="space-y-3">
                  <p>
                    This Privacy Policy explains how Zarito Data Solutions Private Limited (“we”,
                    “us”, “Bargad.ai”) collects, uses, discloses and protects information in
                    connection with the Bargad.ai website, APIs, platform, and business interactions.
                  </p>
                  <p>
                    This document was drafted to be both consumer-facing and compliance-ready - it
                    treats user data with legal precision and practical clarity. For structural and
                    tonal reference we considered industry-standard policy language used by comparable
                    data &amp; risk platforms.
                  </p>
                  <p>
                    Except where otherwise required by law, the personal and business information we
                    process is supplied voluntarily by you (for example, when you request a demo or
                    sign up for API access) and is processed only on the basis of your express
                    consent, contractual necessity, legitimate interest (after balancing), or other
                    lawful bases described below.
                  </p>
                </div>
              </section>

              {/* Regulatory Compliance */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  Regulatory Compliance
                </h2>
                <div className="space-y-3">
                  <p>
                    Bargad.ai, operated by Zarito Data Solutions Private Limited, functions in
                    alignment with prevailing Indian data protection and technology laws and ensures
                    responsible, transparent, and auditable data handling across all its operations.
                  </p>
                  <h3 className="text-white font-semibold mt-4 mb-2">Applicable Frameworks</h3>
                  <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                    <li>
                      Digital Personal Data Protection Act, 2023 (DPDP Act) – Governing principles of
                      consent, purpose limitation, data minimization, and user rights.
                    </li>
                    <li>
                      Information Technology Act, 2000 and IT (Reasonable Security Practices and
                      Procedures and Sensitive Personal Data or Information) Rules, 2011 –
                      Establishing standards for data protection, breach response, and grievance
                      mechanisms.
                    </li>
                    <li>
                      Sectoral Regulations issued by RBI, IRDAI, and other competent authorities –
                      Where relevant to client operations in financial and digital ecosystems.
                    </li>
                  </ul>
                </div>
              </section>

              {/* 1. Definitions */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  1. Definitions
                </h2>
                <p className="mb-3">For the purposes of this Policy:</p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>
                    <span className="text-white font-semibold">
                      Personal Data / Personal Information
                    </span>{' '}
                    - information that identifies or can reasonably identify a natural person (e.g.,
                    name, email, telephone). (only when asked for access)
                  </li>
                  <li>
                    <span className="text-white font-semibold">Client Business Data</span> - datasets,
                    transaction details, flagged cases and other records that our clients provide to
                    Bargad.ai for fraud detection, verification, profiling, or analytics.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Processing</span> - any operation
                    performed on data including collection, storage, access, use, disclosure,
                    analysis, pseudonymization, or deletion.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Controller / Processor</span> -
                    Bargad.ai is a controller for data collected to manage our relationship with
                    website users and customers; for client business data we act under the contract
                    terms as controller, processor, or co-controller depending on the written
                    agreement.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Consent</span> - a freely given,
                    specific, informed and unambiguous indication of the data subject’s wishes,
                    evidenced by a clear affirmative act.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Subprocessor</span> - a third-party
                    service provider engaged by Bargad.ai to process data on our behalf (e.g., cloud
                    hosts, analytics providers).
                  </li>
                </ul>
              </section>

              {/* 2. What we collect */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  2. What we collect
                </h2>
                <p className="mb-3">
                  We limit collection to information necessary for service provisioning, business
                  engagement, security, or where otherwise required by law. Collection categories
                  include:
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      A. Voluntarily provided personal and business information
                    </h3>
                    <p className="mb-2">
                      Collected only when you choose to provide it (e.g., demo request, account
                      creation, API signup):
                    </p>
                    <p className="mb-2">
                      Name, Professional email address and phone number, Business / organization name
                      and business address, Purpose / intended use of our APIs or services, Account
                      credentials (securely stored password hash)
                    </p>
                    <p>
                      <span className="text-white font-semibold">Important:</span> These data elements
                      are collected voluntarily and, where required by law, only with your affirmative
                      consent (e.g., checkbox, consent banner, contract clause). We do not require
                      more data than necessary.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      B. Account registration &amp; authentication data
                    </h3>
                    <ul className="list-disc pl-5 space-y-1 marker:text-[#26ab4d]">
                      <li>
                        Account identifiers, verification metadata, authentication logs, multi-factor
                        authentication status. Passwords are stored only as secure hashes.
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      C. Cookies, tracking &amp; technical telemetry
                    </h3>
                    <ul className="list-disc pl-5 space-y-1 marker:text-[#26ab4d]">
                      <li>
                        Session cookies, persistent cookies, analytics cookies, and similar
                        technologies to measure site usage, secure sessions, and improve product
                        experience. See the Cookies section for granular controls. (incase we write
                        cookies)
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">D. Client Business Data</h3>
                    <ul className="list-disc pl-5 space-y-1 marker:text-[#26ab4d]">
                      <li>
                        Data provided by clients for the purpose of fraud detection, identity
                        verification, risk scoring, model training, or related services. Processing of
                        client business data is performed only under prior consent or contract and
                        only for the purposes instructed by the client.
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">E. System &amp; security logs</h3>
                    <ul className="list-disc pl-5 space-y-1 marker:text-[#26ab4d]">
                      <li>
                        IP addresses, device and browser metadata, request timestamps, error logs and
                        other telemetry necessary for security, troubleshooting and performance
                        monitoring.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* 3. Lawful bases */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  3. Lawful bases for processing
                </h2>
                <p className="mb-3">
                  We process information on one or more of the following lawful grounds:
                </p>
                <ol className="list-decimal pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>
                    <span className="text-white font-semibold">Consent:</span> when you voluntarily
                    provide data (demo request, marketing opt-in, certain cookies, model-training
                    opt-ins). We record the scope and timestamp of consent.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Contractual necessity:</span> to create
                    and manage accounts, provide API access, bill for services, and fulfill other
                    contractual obligations.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Legitimate interests:</span> for
                    security, fraud prevention, product development, and analytics - only after a
                    documented balancing test and providing opt-out mechanisms where required.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Legal obligation:</span> to comply with
                    court orders or statutory duties.
                  </li>
                </ol>
                <p className="mt-3">
                  Where consent is used, we make withdrawal straightforward and effective for future
                  processing (withdrawal will not retroactively invalidate processing already lawfully
                  completed).
                </p>
              </section>

              {/* 4. How we use data */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  4. How we use data
                </h2>
                <p className="mb-3">
                  We use collected data for limited, explicit, and legitimate purposes including but
                  not limited to:
                </p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>Responding to inquiries, scheduling demos, and onboarding customers.</li>
                  <li>
                    Creating and managing user accounts, issuing API credentials, and providing
                    technical support.
                  </li>
                  <li>
                    Performing fraud detection, identity verification, risk scoring and analytics for
                    clients (per client instructions and with prior consent where necessary).
                  </li>
                  <li>
                    Improving product quality, model accuracy, and service reliability using
                    aggregated / pseudonymized datasets; identifiable data is used for model training
                    only with explicit consent or client authorization.
                  </li>
                  <li>
                    Securing systems, detecting abuse, investigating incidents, and protecting the
                    safety of users and services.
                  </li>
                  <li>Meeting legal, regulatory, and contractual obligations.</li>
                </ul>
                <p className="mt-3 text-white font-semibold">
                  We do not sell personal data to third parties.
                </p>
              </section>

              {/* 5. Consent */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  5. Consent
                </h2>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>
                    <span className="text-white font-semibold">Capture:</span> Consent is captured
                    using explicit UI actions (checked boxes, toggles, consent banners), or
                    contractual clauses for enterprise clients.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Recording:</span> We log consent
                    metadata (who, when, scope, method) to demonstrate compliance.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Withdrawal:</span> You can withdraw
                    consent at any time via UI controls, cookie preference center, or by contacting
                    support@bargad.ai. Withdrawal affects the lawfulness of future processing; it does
                    not make prior lawful processing unlawful. Where processing is contractually
                    necessary, withdrawal may affect your access to services.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Granularity:</span> Consent requests
                    are granular - e.g., separate consent for marketing, cookie categories, and model
                    training.
                  </li>
                </ul>
              </section>

              {/* 6. Automated decision-making */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  6. Automated decision-making, profiling &amp; explainability
                </h2>
                <p>
                  Our platform generates automated risk scores and profiles to assist clients in
                  fraud detection. For enterprise clients, profiling occurs under the contract and is
                  used solely for decision support. Where applicable law requires additional
                  safeguards (e.g., a right to human review), we provide mechanisms for review,
                  correction and challenge. Individuals may request an explanation of profiling logic
                  and the opportunity for human intervention by emailing support@bargad.ai.
                </p>
              </section>

              {/* 7. Data sharing */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  7. Data sharing, subprocessors &amp; third-party integrations
                </h2>
                <p className="mb-3">We share data only in narrowly defined situations:</p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>
                    <span className="text-white font-semibold">
                      Subprocessors &amp; service providers:
                    </span>{' '}
                    cloud infrastructure, analytics, identity verification, email delivery and CRM
                    providers - engaged only under written contracts obligating them to process data
                    per our instructions.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Clients &amp; partners:</span> to the
                    extent necessary to deliver contracted services (e.g., performing client-requested
                    fraud checks).
                  </li>
                  <li>
                    <span className="text-white font-semibold">Legal &amp; safety disclosures:</span>{' '}
                    to comply with judicial or regulatory demands, respond to legal process, or
                    protect rights and safety.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Business transfers:</span> in the event
                    of a merger, acquisition or sale we will ensure continued protection of personal
                    data and notify affected users.
                  </li>
                </ul>
                <p className="mt-3">
                  Clients may request a current list of subprocessors by contacting support@bargad.ai.
                  We perform due diligence and require contractual safeguards (security,
                  confidentiality, limited use) before engaging subprocessors.
                </p>
              </section>

              {/* 8. Cross-border transfers */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  8. Cross-border transfers &amp; safeguards
                </h2>
                <p>
                  Because we rely on global cloud infrastructure and third-party processors, your data
                  may be processed or stored outside India. When transfers occur, we implement
                  appropriate technical and contractual safeguards (encryption, access control,
                  standard contractual clauses, or equivalent protections) to maintain an adequate
                  level of protection. Clients may request location-restricted processing where
                  feasible and contractually agreed.
                </p>
              </section>

              {/* 9. Data retention */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  9. Data retention policy
                </h2>
                <p className="mb-3">
                  We retain data only as long as necessary for the stated purpose, to comply with
                  contractual or legal obligations, or to establish, exercise or defend legal claims.
                  Typical retention periods (examples - customizable by client contract):
                </p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>
                    <span className="text-white font-semibold">Account &amp; registration data:</span>{' '}
                    retained while the account is active and typically up to 3 years after account
                    closure.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Demo &amp; sales leads:</span> retained
                    up to 3 years from last engagement unless requested otherwise.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Client business data:</span> retention
                    governed by client agreement and documented instructions.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Logs &amp; telemetry:</span> retained
                    for 12–24 months depending on category and security needs.
                  </li>
                  <li>
                    <span className="text-white font-semibold">Cookies:</span> session cookies expire
                    at session end; persistent cookies vary up to 24 months.
                  </li>
                </ul>
                <p className="mt-3">
                  Requests for custom retention terms for enterprise customers should be included in
                  contractual SOWs.
                </p>
              </section>

              {/* 10. Security controls */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  10. Security controls &amp; incident response
                </h2>
                <p className="mb-3">
                  We implement reasonable organizational and technical safeguards, including:
                </p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>TLS encryption in transit and encryption at rest where appropriate.</li>
                  <li>
                    Role-based access control, privileged access management and least-privilege
                    principles.
                  </li>
                  <li>
                    Secure development lifecycle, vulnerability scanning, and periodic penetration
                    testing.
                  </li>
                  <li>Logging, monitoring and anomaly detection for security events.</li>
                  <li>A documented incident response plan.</li>
                </ul>
                <p className="mt-3">
                  <span className="text-white font-semibold">Breach procedure:</span> On confirming a
                  personal data breach, we will contain and remediate, assess the impact, and notify
                  impacted parties and authorities as required by law — with timely, transparent
                  communications and remediation guidance.
                </p>
              </section>

              {/* 11. Data subject rights */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  11. Data subject rights &amp; how to exercise them
                </h2>
                <p className="mb-3">
                  Subject to applicable law and verification, you may have the right to:
                </p>
                <ul className="list-disc pl-5 space-y-2 marker:text-[#26ab4d]">
                  <li>Access the personal data we hold about you.</li>
                  <li>Rectify inaccurate or incomplete data.</li>
                  <li>
                    Request deletion or restriction of processing (subject to contractual or legal
                    exceptions).
                  </li>
                  <li>Object to processing that relies on legitimate interests.</li>
                  <li>Withdraw consent (for processing that relied on consent).</li>
                  <li>Request portability of your data in a common machine-readable format.</li>
                </ul>
                <p className="mt-3">
                  To exercise rights, contact support@bargad.ai with sufficient detail and proof of
                  identity. We will respond within applicable statutory timeframes and may request
                  further information to verify requests.
                </p>
              </section>

              {/* 12. Children */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  12. Children &amp; minors
                </h2>
                <p>
                  Our services target businesses and adults. We do not knowingly collect data from
                  persons under 18. If we become aware we have collected such data without parental
                  consent, we will promptly delete it and notify the requesting party where required.
                </p>
              </section>

              {/* 13. Marketing */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  13. Marketing communications &amp; opt-out
                </h2>
                <p>
                  We will send marketing or promotional material only if you have given consent or we
                  have another lawful basis. All marketing emails include an easy unsubscribe
                  mechanism; you can alternatively contact support@bargad.ai.
                </p>
              </section>

              {/* 14. Model training */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  14. Model training, de-identification &amp; opt-in controls
                </h2>
                <p>
                  We preferentially use aggregated, pseudonymized or otherwise de-identified datasets
                  for model improvement. Identifiable data will not be used to train production models
                  except with explicit opt-in consent or contractual authorization. Clients may opt
                  out of having their data used for model training via contractual terms.
                </p>
              </section>

              {/* 15. No sale */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  15. No sale of personal data
                </h2>
                <p>
                  Bargad.ai does not sell personal data. Any transfer of personal data is limited to
                  subprocessors under contract, law enforcement requests, or with explicit consent.
                </p>
              </section>

              {/* 16. Legal requests */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  16. Legal requests, law enforcement &amp; compliance
                </h2>
                <p>
                  We comply with lawful requests from courts or regulatory authorities. Where
                  permitted, we will challenge overly broad requests and, when not legally prohibited,
                  notify affected users.
                </p>
              </section>

              {/* 17. Policy changes */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  17. Policy changes &amp; notice
                </h2>
                <p>
                  We may revise this Policy to reflect changes in law, technology or our services.
                  Material changes will be posted with a new effective date and, where feasible,
                  communicated to account holders. Continued use after the effective date signifies
                  acceptance of the updated Policy.
                </p>
              </section>

              {/* 18. Contact */}
              <section>
                <h2 className="text-[1.125rem] sm:text-[1.375rem] font-bold text-[#26ab4d] mb-3">
                  18. Contact, complaints
                </h2>
                <p className="mb-2">
                  For privacy inquiries, complaints or to exercise rights:
                </p>
                <p>
                  <span className="text-white font-semibold">Email:</span>{' '}
                  <a href="mailto:support@bargad.ai" className="text-[#26ab4d] hover:underline">
                    support@bargad.ai
                  </a>
                </p>
                <p>
                  <span className="text-white font-semibold">Address:</span> Zarito Data Solutions
                  Private Limited, Mumbai, India
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
